import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export const TestCaseDetailPage = () => {
  const { testcaseId } = useParams();
  const navigate = useNavigate();
  const [testCase, setTestCase] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTestCase = async () => {
      try {
        const response = await api.get(`/testcases/${testcaseId}`);
        setTestCase(response.data);
      } catch (error) {
        toast.error('Failed to load test case');
      } finally {
        setLoading(false);
      }
    };
    loadTestCase();
  }, [testcaseId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-center">Loading...</div></div>;
  if (!testCase) return <div className="min-h-screen flex items-center justify-center"><div className="text-center"><p className="text-muted-foreground">Test case not found</p><Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button></div></div>;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 gap-2" data-testid="back-button"><ArrowLeft className="h-4 w-4" />Back</Button>
        <Card className="p-8">
          <div className="mb-6 pb-6 border-b border-border">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1.5 bg-azure/10 text-azure rounded font-mono text-lg font-bold">{testCase.tc_id}</span>
              <span className="px-3 py-1 bg-muted rounded text-sm font-medium">{testCase.module}</span>
              {testCase.generated_by_ai && <span className="px-3 py-1 bg-azure/10 text-azure rounded-full text-sm font-medium flex items-center gap-1"><Sparkles className="h-4 w-4" />AI Generated</span>}
            </div>
            <h1 className="text-2xl font-heading font-bold text-blueprint">{testCase.scenario_title}</h1>
          </div>
          <div className="mb-6"><h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">Preconditions</h2><p className="text-base leading-relaxed">{testCase.preconditions}</p></div>
          <div className="mb-6">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Test Steps</h2>
            <div className="space-y-3">{testCase.steps.map((step, index) => (<div key={index} className="flex gap-3"><div className="flex-shrink-0 w-8 h-8 rounded-full bg-azure/10 text-azure flex items-center justify-center font-mono text-sm font-semibold">{index + 1}</div><div className="flex-1 pt-1"><p className="text-base leading-relaxed">{step}</p></div></div>))}</div>
          </div>
          {testCase.test_data && (
            <div className="mb-6" data-testid="testcase-test-data">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">Test Data</h2>
              <pre className="text-base leading-relaxed bg-slate-50 border border-slate-100 rounded-lg p-3 whitespace-pre-wrap font-mono text-[13px] text-slate-800">{testCase.test_data}</pre>
            </div>
          )}
          <div className="mb-6">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Expected Results</h2>
            <div className="space-y-3">{testCase.expected_results.map((result, index) => (<div key={index} className="flex gap-3"><div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-50 text-green-700 flex items-center justify-center font-mono text-sm font-semibold">{index + 1}</div><div className="flex-1 pt-1"><p className="text-base leading-relaxed">{result}</p></div></div>))}</div>
          </div>
        </Card>
      </div>
    </div>
  );
};